import { useState, useRef, useEffect } from 'react';

export interface ArticleLifecycleProps {
  /** True if the article's full content has been fetched. */
  hasFetched: boolean;
  /** True if the article has been enriched (AI analysis complete). */
  hasEnriched: boolean;
  /** True if the article is currently in the enrichment queue (but not yet enriched). */
  isQueued?: boolean;
  /**
   * True if fetching is possible (e.g. a URL exists to fetch from).
   * Defaults to true. When false, the Fetch step shows as locked.
   */
  canFetch?: boolean;
  /** Called when the user clicks the Fetch step. Should throw on failure. */
  onFetch?: () => Promise<void>;
  /** Called when the user clicks the Queue step. Should throw on failure. */
  onEnrich?: () => Promise<void>;
}

/**
 * Displays an article's progress through its lifecycle:
 *   Ingested → Fetched → Queued → Enriched
 *
 * Steps that can be acted on are clickable. Completed steps are shown as done,
 * and expose a dropdown to refetch or re-enrich if callbacks are provided.
 * The component manages its own optimistic state after a successful action so
 * the UI updates immediately without waiting for a parent re-render.
 */
export function ArticleLifecycle({
  hasFetched,
  hasEnriched,
  isQueued = false,
  canFetch = true,
  onFetch,
  onEnrich,
}: ArticleLifecycleProps) {
  const [fetchedOptimistic, setFetchedOptimistic] = useState(false);
  const [queuedOptimistic, setQueuedOptimistic] = useState(false);
  const [enrichedOptimistic, setEnrichedOptimistic] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [queuing, setQueuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchDropdownOpen, setFetchDropdownOpen] = useState(false);
  const [enrichDropdownOpen, setEnrichDropdownOpen] = useState(false);

  const fetchRef = useRef<HTMLDivElement>(null);
  const enrichRef = useRef<HTMLDivElement>(null);

  const isFetched = hasFetched || fetchedOptimistic;
  const isEnriched = hasEnriched || enrichedOptimistic;
  const isInQueue = isQueued || queuedOptimistic;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fetchRef.current && !fetchRef.current.contains(e.target as Node)) {
        setFetchDropdownOpen(false);
      }
      if (enrichRef.current && !enrichRef.current.contains(e.target as Node)) {
        setEnrichDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFetch = async () => {
    if (!onFetch || fetching) return;
    setFetching(true);
    setError(null);
    try {
      await onFetch();
      setFetchedOptimistic(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to fetch content');
    } finally {
      setFetching(false);
    }
  };

  const handleRefetch = async () => {
    setFetchDropdownOpen(false);
    await handleFetch();
  };

  const handleEnrich = async () => {
    if (!onEnrich || queuing) return;
    setQueuing(true);
    setError(null);
    try {
      await onEnrich();
      setQueuedOptimistic(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to queue for enrichment');
    } finally {
      setQueuing(false);
    }
  };

  const handleReenrich = async () => {
    setEnrichDropdownOpen(false);
    await handleEnrich();
  };

  const fetchClickable = !isFetched && canFetch && !!onFetch;
  // Queue button is available once fetched, not yet enriched, and not already queued
  const enrichClickable = isFetched && !isEnriched && !isInQueue && !!onEnrich;

  const fetchClass = isFetched
    ? 'step-done'
    : fetchClickable ? 'step-available' : 'step-locked';

  const enrichClass = isEnriched
    ? 'step-done'
    : isInQueue
      ? 'step-queued'
      : enrichClickable ? 'step-available' : 'step-locked';

  const fetchTitle = isFetched
    ? undefined
    : fetchClickable ? 'Click to fetch full content' : undefined;

  const enrichTitle = isEnriched
    ? undefined
    : isInQueue
      ? 'In enrichment queue'
      : enrichClickable ? 'Click to queue for enrichment'
      : !isFetched ? 'Fetch content first'
      : undefined;

  return (
    <div>
      <div className="lifecycle-stepper">
        <span className="lifecycle-step step-done">Ingested</span>
        <span className="lifecycle-arrow">→</span>

        {isFetched && onFetch ? (
          <div className="lifecycle-dropdown" ref={fetchRef}>
            <button
              className="lifecycle-step step-done lifecycle-step-dropdown"
              onClick={() => setFetchDropdownOpen(o => !o)}
              disabled={fetching}
            >
              {fetching ? 'Fetching…' : 'Fetched'}
              <span className="lifecycle-dropdown-caret">▾</span>
            </button>
            {fetchDropdownOpen && (
              <div className="lifecycle-dropdown-menu">
                <button className="lifecycle-dropdown-item" onClick={handleRefetch}>
                  Refetch
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            className={`lifecycle-step ${fetchClass}`}
            onClick={fetchClickable ? handleFetch : undefined}
            disabled={!fetchClickable || fetching}
            title={fetchTitle}
          >
            {fetching ? 'Fetching…' : isFetched ? 'Fetched' : 'Fetch'}
          </button>
        )}

        <span className="lifecycle-arrow">→</span>

        {isEnriched && onEnrich ? (
          <div className="lifecycle-dropdown" ref={enrichRef}>
            <button
              className="lifecycle-step step-done lifecycle-step-dropdown"
              onClick={() => setEnrichDropdownOpen(o => !o)}
              disabled={queuing}
            >
              {queuing ? 'Queuing…' : 'Enriched'}
              <span className="lifecycle-dropdown-caret">▾</span>
            </button>
            {enrichDropdownOpen && (
              <div className="lifecycle-dropdown-menu">
                <button className="lifecycle-dropdown-item" onClick={handleReenrich}>
                  Re-enrich
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            className={`lifecycle-step ${enrichClass}`}
            onClick={enrichClickable ? handleEnrich : undefined}
            disabled={!enrichClickable || queuing}
            title={enrichTitle}
          >
            {queuing
              ? 'Queuing…'
              : isEnriched
                ? 'Enriched'
                : isInQueue
                  ? 'Queued'
                  : 'Queue'}
          </button>
        )}
      </div>
      {error && (
        <div className="site-fetch-error" style={{ marginTop: 6, fontSize: '0.8rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
